import _ from 'lodash';
import React from 'react';
import { useState } from 'react';
import { useLocation } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { Assets, Colors, FontFamily, Models } from './imports.utils';
import * as THREE from "three";
import { ButtonStyles, FontFamilyTypes, FontStyles, IExpiryStatus, IImageTargetManifest, IObject, ObjectTypes, ObjectTypesImages, ProjectStatus, PropertiesTypes, Status, TargetImageData, TargetImageObject} from './interface.utils';
import { PROJECT_IMAGES_FALLBACK_FOLDER_NAME, PROJECT_IMAGES_FOLDER_NAME_MAX_LENGTH, TARGET_IMAGE_DIMENSIONS_CANVAS, TARGET_IMAGE_JPEG_QUALITY, TARGET_IMAGE_MIN_HEIGHT, TARGET_IMAGE_MIN_WIDTH, TARGET_QR_BACKING_COLOR, TARGET_QR_INSET_RATIO, TARGET_QR_LOGO_PADDING_RATIO, TARGET_QR_LOGO_RATIO, TARGET_QR_MIN_SIZE_PX, TARGET_QR_SIZE_RATIO, ADMIN_PATH_PREFIXES, USER_ONLY_PATH_PREFIXES, ADMIN_NAV_ITEMS, VIDEO_MAX_SIZE_MB } from './constant.utils';
import { ProjectStatusKey, ProjectTargetRecord } from 'interfaces/project.interface';
import { QuoteTargetInfo } from 'interfaces/payment_modal.interface';
import { ParsedVisitCheck, VisitCheckResponse } from 'interfaces/visit_check.interface';
import { EditorEmptyStateMode } from 'interfaces/editor_empty_state.interface';
import { SavedProjectDataRow } from 'interfaces/project_data.interface';
import { Invoice } from 'interfaces/invoice.interface';
import { VideoControlIconState } from 'interfaces/video_control.interface';
import { QrLogoPlacement, TargetImageSource, TargetQrPlacement } from 'interfaces/target_qr_download.interface';


export const getStoredUser = () => {
  const raw = localStorage.getItem('user');
  if (!raw) return {};
  try {
    return JSON.parse(raw);
  } catch {
    localStorage.removeItem('user');
    return {};
  }
};

// Whether `pathname` is one of the admin screens (`/admin`, `/admin/users/...`).
export const isAdminPath = (pathname: string): boolean =>
  ADMIN_PATH_PREFIXES.some((prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`));

// Whether `pathname` is a screen only a normal user may open (`/welcome`, `/support`, `/upgrade-plan`).
export const isUserOnlyPath = (pathname: string): boolean =>
  USER_ONLY_PATH_PREFIXES.some((prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`));

// How the admin screens identify a user: the username, else the email (the two fields every account is created with).
export const getAdminUserLabel = (user: { username?: string; email?: string }): string =>
  user.username || user.email || '';

// Which admin sidebar section a path belongs to (`/admin` and `/admin/overview` are Overview); null outside admin.
export const getAdminNavKey = (pathname: string): 'overview' | 'users' | 'projects' | 'tickets' | null => {
  if (pathname === '/admin') return 'overview';
  const item = ADMIN_NAV_ITEMS.find((nav) => pathname === nav.route || pathname.startsWith(`${nav.route}/`));
  return item ? item.key : null;
};

// UX-only gate: the role the server returned at login. The server enforces admin access on every call;
// this only keeps non-admins from landing on screens that would 403 on every request.
export const getStoredRole = (): string | undefined => {
  const role = getStoredUser().role;
  return typeof role === 'string' ? role : undefined;
};

// True only for the account the server names in UNLIMITED_USER_EMAILS: it works without the product's
// limits and is never asked to pay. The server tells us with `is_unlimited: true` on the user it returns
// at login / signup, which is kept in localStorage. This only decides what the browser hides or skips;
// every limit and the payment itself are enforced on the server, so editing it here gains nothing.
export const isUnlimitedUser = (): boolean => getStoredUser().is_unlimited === true;

// publish_project resolves with the server's response body. Only a missing body or an explicit
// status of 'failed' counts as a failure; HTTP errors reject instead and are caught by the caller.
export const isPublishSuccessful = (response: unknown): boolean => {
  if (typeof response !== 'object' || response === null) return false;
  const status: unknown = Reflect.get(response, 'status');
  return status !== 'failed';
};

// First letter of the first and last word of a name, uppercased: "jane doe" -> "JD", "kishore" -> "K".
// Returns '' when there is no usable name. Array.from keeps characters made of two UTF-16 code units whole.
export const getInitials = (name?: string | null): string => {
  const words = (name ?? '').split(/\s+/).filter(Boolean);
  const ends = words.length > 1 ? [words[0], words[words.length - 1]] : words;
  return ends.map((word) => Array.from(word)[0].toUpperCase()).join('');
};

// A project can only be published once it has a target with its target image attached. What the
// target holds does not matter: whichever object types it has, it is publishable.
export const hasTargetImage = (targets: ProjectTargetRecord[]): boolean =>
  targets.some((target) => Boolean(target.data.targetImage));

// The one status rule the row pill and the welcome screen filter share (the server applies the same rule to count and filter).
export const getProjectStatusKey = (isPublished: boolean, hasTarget: boolean): ProjectStatusKey =>
  isPublished ? 'live' : hasTarget ? 'ready' : 'needs';

// How many target images a project has: the targets with an image attached, whether or not they are compiled
// yet (a target without a targetId still counts, unlike in the payment id list).
export const countTargetImages = (targets: ProjectTargetRecord[]): number =>
  targets.filter((target) => Boolean(target.data.targetImage)).length;

// What a price quote line shows about its target, by the target's server id: the quote only knows the id, so the
// picture and the number of objects on it come from the project's own data. Targets without an id are left out.
export const getQuoteTargetsById = (targets: ProjectTargetRecord[]): Record<string, QuoteTargetInfo> => {
  const infoById: Record<string, QuoteTargetInfo> = {};
  targets.forEach((target) => {
    if (!target.data.targetId) return;
    infoById[target.data.targetId] = { imageUrl: target.data.targetImage, objectCount: target.data.objects?.length ?? 0 };
  });
  return infoById;
};

export const getBaseURL = () => {
  let baseURL = 'https://api.picpopstudio.com';
  if (process.env.REACT_APP_NODE_ENV === 'development') {
    baseURL = window.location.protocol === 'https:'
      ? `https://${window.location.hostname}:9443`
      : `http://${window.location.hostname}:9000`;
  } else if (process.env.REACT_APP_NODE_ENV === 'stage') {
    baseURL = 'https://askhugo-client.augmo.io';
  }
  return baseURL;
};

export const getDomainURL = () => {
  let baseURL = 'https://app.picpopstudio.com';
  if (process.env.REACT_APP_NODE_ENV === 'development') {
    // "localhost" isn't reachable from a phone scanning the preview QR code, so prefer the
    // configured LAN IP (REACT_APP_LAN_HOST) whenever the page itself was loaded via localhost.
    // Otherwise match whatever protocol/host/port the page was actually loaded on (e.g. the
    // HTTPS LAN preview on :3002) instead of a hardcoded :3000.
    const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
    const hostname = isLocalhost && process.env.REACT_APP_LAN_HOST ? process.env.REACT_APP_LAN_HOST : window.location.hostname;
    baseURL = `${window.location.protocol}//${hostname}:${window.location.port}`;
  }
  return baseURL;
};

// Public, unauthenticated -- ar.screen.tsx needs no auth token (it uses the public project_data/resolve
// endpoint), and this link is meant to be scanned by anonymous visitors, so it must never carry the
// link-creator's own session token. Shared by the preview screen and the project list's image download, so
// the QR code on a downloaded image always encodes the same link the preview shows.
export const getProjectArLink = (projectId: string): string => `${getDomainURL()}/ar?project_id=${projectId}`;

export const useSetState = (initialState: any) => {
  const [state, setState] = useState(initialState);

  const newSetState = (newState: any) => {
    setState((prevState: any) => ({ ...prevState, ...newState }));
  };
  return [state, newSetState];
};

export const Failure = (text: string) => {
  console.log(text);
};

export const Success = (text: string) => {
  console.log(text);
};

export const modelError = (error: any) => {
  console.log(JSON.stringify(error.response));
  if (error.response.data.message) {
    return error.response.data.message;
  } else if (error.message) {
    return error.message;
  } else if (error.response) {
    return error.response;
  } else {
    return error;
  }
};

export const toastifyError = (text?: any) => {
  if (!_.isEmpty(text)) {
    toast.error(text, {
      duration: 4000,
      position: "top-center",
      style: {
        fontStyle: "capitalize",
        fontFamily: FontFamily.medium,
        fontSize: "12px",
        color: Colors.toastErrorText,
        background: Colors.toastErrorBackground,
        borderRadius: "8px"
      },
      iconTheme: {
        primary: Colors.toastErrorText,
        secondary: Colors.toastErrorBackground
      },
      // Aria
      ariaProps: {
        role: "status",
        "aria-live": "polite",
      },
    });
  }
};

export const toastifySuccess = (text?: any) => {
  if (!_.isEmpty(text)) {
    toast.success(text, {
      duration: 4000,
      position: "top-center",
      style: {
        fontStyle: "capitalize",
        fontFamily: FontFamily.medium,
        fontSize: "12px",
        color: Colors.toastSuccessText,
        background: Colors.toastSuccessBackground,
        borderRadius: "8px"
      },
      iconTheme: {
        primary: Colors.toastSuccessText,
        secondary: Colors.toastSuccessBackground
      },
      // Aria
      ariaProps: {
        role: "status",
        "aria-live": "polite",
      },
    });
  }
};



export const uploadFile = (file, signedRequest, url, cb) => {
  const xhr = new XMLHttpRequest();
  xhr.open('PUT', signedRequest);
  xhr.onreadystatechange = () => {
    if (xhr.readyState === 4) {
      if (xhr.status === 200) {
        let data = {
          url: url,
        };
        cb(data);
      } else {
        cb({ error: 'Upload failed' });
      }
    }
  };
  xhr.send(file);
};

export const useQuery = () => {
  const { search } = useLocation();
  return React.useMemo(() => new URLSearchParams(search), [search]);
};

export const getDropdownData = (data: any) => {
  let array: any = [];
  data?.map((item: string) => array.push({ label: item, value: item }));
  return array;
};

export const getDataFromDropdown = (data: any) => {
  let array: any = [];
  data?.map((item: any) => array.push(item.value));
  return array;
};

export const getDropdownValue = (data: string) => {
  if (!_.isEmpty(data)) {
    return { label: data, value: data };
  } else {
    return { label: '', value: '' };
  }
};

export const useAuth = () => {
  const token = localStorage.getItem('token');
  if (token) {
    return true;
  } else {
    return false;
  }
};

export const filterObjectByKeys = (object = {}, keys) => {
  return _.omit(object, keys);
};

export const filterByKeys = (obj, keys: any = []) => {
  const filtered: any = {};
  keys.forEach((key) => {
    if (obj?.hasOwnProperty(key)) {
      filtered[key] = obj[key];
    }
  });
  return filtered;
};

export const mergeArrayWithoutDuplicates = (array1: any, array2: any) => {
  if (array1.length == 0) {
    return array2;
  } else if (array2.length == 0) {
    return array1;
  } else if (array1.length > 0 && array2.length > 0) {
    array1.map((element1: any, index1: number) => {
      array2.map((element2: any, index2: number) => {
        if (element1._id == element2._id) {
          array1[index1] = array2[index2];
        } else {
        }
      });
    });
    return Array.from(new Set([...array1, ...array2]));
  }
};

export const gets3FileName = (file: any) => {
  let filename: string = file.split('/').pop();
  return filename.split('_').pop();
};

export const getIndex = (id: string, array: any[]) => array.findIndex((item: any) => item.id === id)


export const getFontFamily = (fontFamily: string, style: string): string => {
  // Guard against undefined or null input
  if (!fontFamily) return "Montserrat-Regular";

  let styleSuffix = "Regular"; // Default fallback

  if (style && style.includes("bold")) {
    styleSuffix = "Bold";
  } else if (style && style.includes("italic")) {
    styleSuffix = "Italic";
  }

  return `${fontFamily}-${styleSuffix}`;
};

export const disposeMaterial = (material: THREE.Material) => {
  // Dispose of any maps on the material
  if (material instanceof THREE.MeshBasicMaterial) {
    if (material.map) material.map.dispose();
  }
  // Dispose of the material itself
  material.dispose();
};

export const getDefaultStateForObjectType = (type: string) => {
  switch (type) {
    case ObjectTypes.IMAGE:
      return {
        src: null,
        objectId: null,
        positionX: 0,
        positionY: 0,
        positionZ: 0,
        rotationX: 0,
        rotationY: 0,
        rotationZ: 0,
        scaleX: 1,
        scaleY: 1,
        scaleZ: 1,
        renderOrder: 0,
      }
    case ObjectTypes.VIDEO:
      return {
        src: null,
        objectId: null,
        positionX: 0,
        positionY: 0,
        positionZ: 0,
        rotationX: 0,
        rotationY: 0,
        rotationZ: 0,
        scaleX: 1,
        scaleY: 1,
        scaleZ: 1,
        renderOrder: 0,
      }
    case ObjectTypes.BUTTON:
      return {
        presets: {
          name: ButtonStyles.PRIMARY_WITH_BORDER_RADIUS,
          styles: {
            inActive: { backgroundColor: Colors.fontPrimary, borderRadius: "8px", color: Colors.white },
            active: { backgroundColor: Colors.primaryColor, borderRadius: "8px", color: Colors.onPrimary },
            passed: { backgroundColor: Colors.fontPrimary, borderRadius: 8, color: Colors.white }
          }
        },
        selectedPreset: { backgroundColor: Colors.fontPrimary, borderRadius: 8, color: Colors.white },
        fontFamily: FontFamilyTypes.MONTSERRAT,
        url: "",
        text: "Text",
        fontSize: { label: "12", value: "12" },
        fontStyle: FontStyles.BOLD,
        fontColor: Colors.white,
        bgColor: "#484848",
        positionX: 0,
        positionY: 0,
        positionZ: 0,
        rotationX: 0,
        rotationY: 0,
        rotationZ: 0,
        scaleX: 1,
        scaleY: 1,
        scaleZ: 1,
        renderOrder: 0,
      }
    case ObjectTypes.TEXT:
      return {
        text: "Mushroom villa",
        fontSize: { label: "48", value: "48" },
        fontFamily: FontFamilyTypes.MONTSERRAT,
        fontStyle: FontStyles.BOLD,
        fontColor: Colors.black,
        bgColor: Colors.transparent,
        positionX: 0,
        positionY: 0,
        positionZ: 0,
        rotationX: 0,
        rotationY: 0,
        rotationZ: 0,
        scaleX: 1,
        scaleY: 1,
        scaleZ: 1,
        renderOrder: 0,
      }
    case ObjectTypes.SOCIAL:
      return {
        assetSrc: Assets.social_yt,
        url: "",
        color: "#C60000",
        bgColor: Colors.white,
        positionX: 0,
        positionY: 0,
        positionZ: 0,
        rotationX: 0,
        rotationY: 0,
        rotationZ: 0,
        scaleX: TARGET_IMAGE_DIMENSIONS_CANVAS * 0.05,
        scaleY: TARGET_IMAGE_DIMENSIONS_CANVAS * 0.05,
        scaleZ: 1,
        renderOrder: 0,
      }
    default:
      return {}
  }
}

// A button object persists only the flat `passed` styles of its preset -- the Properties panel's
// sync-back effect saves `presets: state.button.selectedPreset`, i.e. `style.styles.passed` -- so
// the preset's *name*, the one thing the panel's highlight is keyed on, never reaches saved data.
// Recover the full descriptor by matching those stored styles against the known preset list; each
// preset's `passed` block is distinct, so the match is unambiguous. Matching rather than persisting
// the name keeps every already-saved project working without a data migration.
export const findButtonPresetByStyles = <T extends { styles: { passed: IObject } }>(
  presets: Array<T>,
  storedPresetStyles: IObject | null | undefined,
): T | undefined => {
  if (!storedPresetStyles) return undefined;
  return presets.find((preset) => _.isEqual(preset.styles.passed, storedPresetStyles));
}

export const getImageDimension = (file: File) => {
  return new Promise((resolve: any, reject: any) => {
    const image = new Image();
    image.onload = () => {
      resolve({
        width: image.width,
        height: image.height
      });
    };

    image.onerror = () => {
      reject(new Error("Failed to load image"));
    }

    image.src = URL.createObjectURL(file);
  })
}

export const getVideoDimension = (file: File): Promise<{ width: number; height: number }> => {
  return new Promise((resolve, reject) => {
    const video = document.createElement("video");

    video.onloadedmetadata = () => {
      resolve({
        width: video.videoWidth,
        height: video.videoHeight,
      });
      URL.revokeObjectURL(video.src); // Clean up
    };

    video.onerror = () => {
      reject(new Error("Failed to load video"));
    };

    video.src = URL.createObjectURL(file);
    video.load(); // Required in some browsers to trigger `onloadedmetadata`
  });
};

export const convertImageUrlToFile = async (imageUrl: string | null, fileName: string = 'target-image.mind') => {
  try {
    const response = await fetch(imageUrl ?? '');
    const blob = await response.blob();

    return new File([blob], fileName, { type: blob.type });;
  } catch (error) {
    console.error("Failed to convert Image URL to file:", error);
    throw error;
  }
}

const bufferToHexHash = async (buffer: ArrayBuffer): Promise<string> => {
  const digest = await crypto.subtle.digest('SHA-256', buffer);
  return Array.from(new Uint8Array(digest)).map((byte) => byte.toString(16).padStart(2, '0')).join('');
}

export const hashFile = async (file: File): Promise<string> => bufferToHexHash(await file.arrayBuffer());

const hashImageUrl = async (imageUrl: string): Promise<string> => {
  const response = await fetch(imageUrl);
  const buffer = await response.arrayBuffer();
  return bufferToHexHash(buffer);
}

// PAYMENT.md/ISSUES.md #1 -- the backend's own duplicate check is scoped to a single Save's
// compile batch (see augmo-backend target.service.ts), which only fires later, on Save. This
// gives the same protection at the point the user actually picks the image, comparing against
// every other target already in the project (excluding the target currently being edited, since
// re-uploading the same image onto itself is a legitimate replace, not a duplicate).
export const isDuplicateTargetImage = async (
  file: File,
  targetImageDataList: Array<{ id: string; targetImage: string | null }>,
  excludeTargetImageId: string | null
): Promise<boolean> => {
  const otherTargetImages = targetImageDataList
    .filter((target) => target.id !== excludeTargetImageId && !!target.targetImage)
    .map((target) => target.targetImage as string);
  if (otherTargetImages.length === 0) return false;

  const newFileHash = await hashFile(file);
  const existingHashes = await Promise.all(otherTargetImages.map((url) => hashImageUrl(url)));
  return existingHashes.includes(newFileHash);
}

export const compareObjectProperties = (currentProps: any, previousProps: any): boolean => {
  if (!currentProps || !previousProps) return false;
  
  // Get all keys from both objects
  const currentKeys = Object.keys(currentProps);
  const previousKeys = Object.keys(previousProps);
  
  // Check if number of properties is different
  if (currentKeys.length !== previousKeys.length) return false;
  
  // Check each property value
  for (const key of currentKeys) {
    const currentValue = currentProps[key];
    const previousValue = previousProps[key];
    
    // Handle null/undefined cases
    if (currentValue === null && previousValue === null) continue;
    if (currentValue === undefined && previousValue === undefined) continue;
    if (currentValue === null || currentValue === undefined || 
        previousValue === null || previousValue === undefined) {
      return false;
    }
    
    // If both values are objects (but not null), compare them recursively
    if (typeof currentValue === 'object' && typeof previousValue === 'object') {
      if (!compareObjectProperties(currentValue, previousValue)) {
        return false;
      }
    } else {
      // For primitive values, use strict equality
      if (currentValue !== previousValue) {
        return false;
      }
    }
  }
  
  return true;
};

export const handlePayment = async (currentUser: any, projectId: string, targetIds: string[], state: any, setState: any) => {

    if (!state.isRazorpayLoaded || !window.Razorpay) {
      toastifyError("Payment system not loaded. Please try again");
      return;
    }

    try {
      // PAYMENT.md Phase 3 -- the server computes and returns the amount; the client never
      // supplies a price, currency, or user id here (the payer is read from the verified JWT).
      const paymentBody = {
        project_id: projectId,
        target_ids: targetIds,
      }
      const order: any = await Models.auth.createPaymentOrder(paymentBody);
  
      if (order && order.data) {
        const options = {
          key : order.data.key_id,
          amount: order.data.amount,
          currency: order.data.currency,
          name: "Brown Button Private Limited",
          description: "Test Transaction",
          image: Assets.logo,
          order_id: order.data.order_id,
          handler: (response: any) => handlePaymentVerification(response, projectId, setState),
          prefill: {
            name: currentUser.name ?? "",
            email: currentUser.email ?? "",
          },
          notes: { address: "Razorpay Corporate Office" },
          theme: {
            color: Colors.primaryTextColor,
          },
          modal: {
            ondismiss: () => handlePaymentModalClose(order.data.order_id)
          }
        }
        const razorpayInstance = new window.Razorpay(options);
        razorpayInstance.open();
      }
    } catch (error) {
      console.error('Payment failed:', error);
      toastifyError('Payment failed');
    }
  }

  const handlePaymentVerification = async (response: any, projectId: string, setState: any) => {
    const paymentVerificationBody = {
      razorpay_payment_id: response.razorpay_payment_id,
      razorpay_order_id: response.razorpay_order_id,
      razorpay_signature: response.razorpay_signature,
    }

    // Publishing now happens server-side, inside verify_payment/webhook (PAYMENT.md Phase 6
    // item 1) -- both the callback and webhook paths deliver the product this way, so this
    // handler only needs to react to the already-published result, not trigger it.
    try {
      const verificationResponse: any = await Models.auth.verifyPayment(paymentVerificationBody);
      if (verificationResponse.data.status === Status.SUCCESS) {
        setState({ isPublishedModalOpen: true, isCurrentProjectNotPublished: false });
        toastifySuccess('Published Successfully!');
      } else {
        toastifyError('Published Failed');
      }
    } catch (error) {
      console.error('Payment verification failed:', error);
      toastifyError('Published Failed');
    }
  }

  const handlePaymentModalClose = async (orderId: string) => {
    try {
      const paymentStatusUpdateBody = {
        order_id: orderId,
        status: Status.FAILED,
      }
      const res: any = await Models.auth.updatePaymentStatus(paymentStatusUpdateBody);
      if (res && res.data) {
        toastifyError("Payment cancelled");
      }
    } catch (error) {
      console.error('Error updating payment status:', error);
    }
  }

// PAYMENT.md Phase 5 item 3 -- reuses the same Razorpay-open -> verify_payment path as
// handlePayment above; the only difference is which endpoint creates the order and that
// there's no project to publish afterward, just an extended expires_at to report back.
export const handleRenewal = async (currentUser: any, targetId: string, isRazorpayLoaded: boolean, onRenewed: () => void) => {
  if (!isRazorpayLoaded || !window.Razorpay) {
    toastifyError("Payment system not loaded. Please try again");
    return;
  }

  try {
    const order: any = await Models.auth.createRenewalOrder({ target_id: targetId });

    if (order && order.data) {
      const options = {
        key: order.data.key_id,
        amount: order.data.amount,
        currency: order.data.currency,
        name: "Brown Button Private Limited",
        description: "Target renewal",
        image: Assets.logo,
        order_id: order.data.order_id,
        handler: async (response: any) => {
          const paymentVerificationBody = {
            razorpay_payment_id: response.razorpay_payment_id,
            razorpay_order_id: response.razorpay_order_id,
            razorpay_signature: response.razorpay_signature,
          }
          try {
            const verificationResponse: any = await Models.auth.verifyPayment(paymentVerificationBody);
            if (verificationResponse.data.status === Status.SUCCESS) {
              toastifySuccess('Renewed successfully!');
              onRenewed();
            } else {
              toastifyError('Renewal failed');
            }
          } catch (error) {
            console.error('Renewal verification failed:', error);
            toastifyError('Renewal failed');
          }
        },
        prefill: {
          name: currentUser.name ?? "",
          email: currentUser.email ?? "",
        },
        theme: {
          color: Colors.primaryTextColor,
        },
      }
      const razorpayInstance = new window.Razorpay(options);
      razorpayInstance.open();
    }
  } catch (error) {
    console.error('Renewal failed:', error);
    toastifyError('Renewal failed');
  }
}

export const scaleCanvasPositionToARCoords = (xAxis: number, yAxis: number, targetWidth: number, targetHeight: number, scaleFactor: { width: number, height: number }) => {
  return {
    x: (xAxis * (targetWidth / scaleFactor.width)) / targetWidth,
    y: (yAxis * (targetHeight / scaleFactor.height)) / targetHeight,
  }
}

export const scaleSizeToARCoordsSocial = (xAxis: number, yAxis: number, scaleFactor: number) => {
  return {
    x: xAxis / scaleFactor,
    y: yAxis / scaleFactor,
  }
}

// A target's containing group is scaled non-uniformly (containerScaleX/containerScaleY) to match
// the tracked image's own aspect ratio. A geometry built with equal width/height inherits that
// non-uniform scale and renders as an ellipse, not a circle. To land back on a true circle on
// screen we need geomWidth * scaleX == geomHeight * scaleY, i.e. the height factor must be
// scaleX / scaleY (not scaleY / scaleX, which makes the distortion worse).
export const computeCircularAspectCorrection = (containerScaleX: number, containerScaleY: number): number => {
  return containerScaleY !== 0 ? containerScaleX / containerScaleY : 1;
}

// AR video tap-to-toggle: while paused the play icon stays visible so the user knows to tap;
// while playing the pause icon only flashes, so nothing covers the video content.
export const resolveVideoControlIcon = (isPlaying: boolean): VideoControlIconState => {
  return isPlaying ? { icon: 'pause', persistent: false } : { icon: 'play', persistent: true };
}

export const scaleCanvasSizeToARCoords = (xAxis: number, yAxis: number, targetWidth: number, targetHeight: number, scaleFactor: { width: number, height: number }) => {
  return {
    x: ((xAxis / scaleFactor.width) * targetWidth) / targetWidth,
    y: ((yAxis / scaleFactor.height) * targetHeight) / targetHeight,
    z: 1
  }
}

export const computeCappedCanvasSize = (
  cssWidth: number,
  cssHeight: number,
  devicePixelRatio: number,
  maxDimension: number
): { width: number; height: number } => {
  let width = cssWidth * devicePixelRatio;
  let height = cssHeight * devicePixelRatio;

  if (height > maxDimension) {
    width = maxDimension * (width / height);
    height = maxDimension;
  }
  if (width > maxDimension) {
    height = maxDimension * (height / width);
    width = maxDimension;
  }

  return { width: Math.round(width), height: Math.round(height) };
}

export const parseCompiledManifests = (complied: string | null): IImageTargetManifest[] => {
  if (!complied) return [];
  try {
    const parsed = JSON.parse(complied);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

// Save deletes every project_data row of the project and recreates them with new _ids, so the ids the
// editor holds go stale. Points each target at the row Save just created for it (matched by the target's
// own id, not by position); a target with no matching row keeps what it had.
export const withSavedProjectDataIds = (targets: TargetImageData[], savedRows: SavedProjectDataRow[] | null | undefined): TargetImageData[] => {
  if (!Array.isArray(savedRows)) return targets;
  return targets.map((target) => {
    const row = savedRows.find((savedRow) => savedRow?._id && savedRow.data?.id === target.id);
    return row ? { ...target, projectDataId: row._id } : target;
  });
}

export const hasObjectOfType = (objects: TargetImageObject[], type: ObjectTypes, excludeObjectId?: string): boolean => {
  return objects.some((object) => object.type === type && object.id !== excludeObjectId);
}

// The icon for an object type, narrowing the plain string the store keeps to a known type. An unknown type
// falls back to the image icon rather than leaving the icon undefined.
export const getObjectTypeImage = (type: string): string => {
  const knownType = Object.values(ObjectTypes).find((objectType) => objectType === type);
  return knownType ? ObjectTypesImages[knownType] : ObjectTypesImages[ObjectTypes.IMAGE];
}

// "image" -> "Image". An empty type reads as an empty label rather than throwing.
export const getObjectTypeLabel = (type: string): string => {
  return type ? type.charAt(0).toUpperCase() + type.slice(1) : '';
}

// "Image 2": the type plus how many objects of that type there are from this one to the end of the list.
// New objects are added at the top, so counting from the bottom up keeps the first image added "Image 1".
export const getObjectDisplayName = (objects: TargetImageObject[], index: number): string => {
  if (!objects[index]) return '';
  const type = objects[index].type;
  const ordinal = objects.slice(index).filter((object) => object.type === type).length;
  return `${getObjectTypeLabel(type)} ${ordinal}`.trim();
}

const OBJECT_ALLOWED_EXTENSIONS: Record<'image' | 'video', string[]> = {
  image: ['jpg', 'jpeg', 'png', 'webp'],
  video: ['mp4'],
}

const VIDEO_MAX_SIZE_BYTES = VIDEO_MAX_SIZE_MB * 1024 * 1024;

export const validateObjectFilePreflight = (file: File, uploadType: 'image' | 'video'): string | null => {
  const extension = file.name.split('.').pop()?.toLowerCase() ?? '';

  if (!OBJECT_ALLOWED_EXTENSIONS[uploadType].includes(extension)) {
    return 'Unsupported or unrecognized file format';
  }

  if (uploadType === 'video' && !isUnlimitedUser() && file.size > VIDEO_MAX_SIZE_BYTES) {
    return `Video exceeds the ${VIDEO_MAX_SIZE_MB}MB limit`;
  }

  return null;
}

// Checking the minimum here lets a too-small target image get rejected at upload time, where the
// user can see it's the target image that's invalid, instead of surfacing as an opaque "Invalid
// crop geometry" error later when an object image/video is uploaded and compile-on-demand runs
// for the first time.
export const validateTargetImageDimensions = (dimensions: { width: number; height: number }): string | null => {
  if (dimensions.width < TARGET_IMAGE_MIN_WIDTH || dimensions.height < TARGET_IMAGE_MIN_HEIGHT) {
    return `Image is too small: minimum size is ${TARGET_IMAGE_MIN_WIDTH}x${TARGET_IMAGE_MIN_HEIGHT}`;
  }
  return null;
}

// The placeholder a new target starts as, before its image is uploaded from the Properties panel.
export const buildEmptyTargetImage = (id: string): TargetImageData => {
  return {
    id,
    targetImage: null,
    targetId: null,
    containedTargetImageDimension: { width: 0, height: 0 },
    selectedTargetImageDimension: { width: 0, height: 0 },
    objects: [],
  };
}

// The editor only renders the workspace for a selected target that has an image. Any other state
// leaves that slot blank, so the guided empty-state card takes it: 'addTarget' while the project
// has no targets at all, 'uploadImage' while targets exist but there is no image to work on.
export const getEditorEmptyStateMode = (targetImageDataList: TargetImageData[], selectedTargetIndex: number | null): EditorEmptyStateMode => {
  if (targetImageDataList.length === 0) return 'addTarget';
  if (selectedTargetIndex !== null && targetImageDataList[selectedTargetIndex]?.targetImage) return 'hidden';
  return 'uploadImage';
}

// The empty-state card only talks about a "first" target image while no target in the project
// has an image; after that, an image-less target is just another one being added.
export const isFirstTargetImage = (targetImageDataList: TargetImageData[]): boolean => {
  return !targetImageDataList.some((targetImageData) => !!targetImageData.targetImage);
}

// Which target the empty-state card's button uploads into when targets already exist: the
// selected one, else the first still waiting for an image, else the first. Null with no targets,
// where the button adds one instead.
export const getEmptyStateUploadTargetIndex = (targetImageDataList: TargetImageData[], selectedTargetIndex: number | null): number | null => {
  if (targetImageDataList.length === 0) return null;
  if (selectedTargetIndex !== null && targetImageDataList[selectedTargetIndex]) return selectedTargetIndex;
  const firstWithoutImageIndex = targetImageDataList.findIndex((targetImageData) => !targetImageData.targetImage);
  return firstWithoutImageIndex === -1 ? 0 : firstWithoutImageIndex;
}

// PAYMENT.md Phase 3 -- display only, never used to compute a charge. The server's price_quote
// and create_payment_order responses are the only source of the amount.
export const formatPaiseToRupees = (amountPaise: number): string => {
  const rupees = amountPaise / 100;
  return Number.isInteger(rupees) ? `₹${rupees}` : `₹${rupees.toFixed(2)}`;
}

// Display only, like formatPaiseToRupees, but always with paise ("₹129.00"): the order summary stacks amounts
// that are whole rupees next to ones that are not, and the column only lines up when they all show two decimals.
export const formatPaiseToRupeesExact = (amountPaise: number): string => `₹${(amountPaise / 100).toFixed(2)}`;

const INVOICE_MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

// An invoice's issue date as "05 Oct 2026", in Indian time: an invoice is an Indian GST document, so the day
// must not shift with the viewer's timezone. The month name comes from a fixed list because Intl spells
// September "Sept" in some locales. A missing or unparseable date reads "N/A".
export const formatInvoiceDate = (isoDate?: string | null): string => {
  if (!isoDate) return 'N/A';
  const date = new Date(isoDate);
  if (Number.isNaN(date.getTime())) return 'N/A';
  const parts = new Intl.DateTimeFormat('en-GB', { timeZone: 'Asia/Kolkata', day: '2-digit', month: 'numeric', year: 'numeric' }).formatToParts(date);
  const part = (type: string): string => parts.find((entry) => entry.type === type)?.value ?? '';
  return `${part('day')} ${INVOICE_MONTHS[Number(part('month')) - 1]} ${part('year')}`;
}

// Appends a freshly loaded page to the invoices already shown, in order, skipping any already on screen
// (a page boundary can overlap when a new invoice lands between two loads).
export const mergeInvoicePages = (existing: Invoice[], incoming: Invoice[]): Invoice[] => {
  const seenIds = new Set(existing.map((invoice) => invoice._id));
  const merged = [...existing];
  incoming.forEach((invoice) => {
    if (!seenIds.has(invoice._id)) {
      seenIds.add(invoice._id);
      merged.push(invoice);
    }
  });
  return merged;
}

// Whether "Load more" shows: only while the server holds more invoices than are loaded.
export const hasMoreInvoices = (loadedCount: number, totalDocs?: number): boolean => {
  return typeof totalDocs === 'number' && loadedCount < totalDocs;
}

// The target images, each with the project's QR code on it, can be downloaded only once the
// project is both paid for and published -- and only if there is an image to download.
export const canDownloadProjectImages = (projectPaymentStatus: boolean, projectStatus: string, targetImageCount: number): boolean => {
  return projectPaymentStatus && projectStatus === ProjectStatus.PUBLISHED && targetImageCount > 0;
}

// Where the QR box goes on a target image of the given size, and the size the image is written
// at. The box is a share of the shorter side, so it looks the same on portrait and landscape
// images. An image too small for a scannable box is scaled up, keeping its aspect ratio.
export const getTargetQrPlacement = (imageWidth: number, imageHeight: number): TargetQrPlacement => {
  if (!(imageWidth > 0) || !(imageHeight > 0)) {
    throw new Error(`Invalid target image size: ${imageWidth}x${imageHeight}`);
  }
  const scale = Math.max(1, TARGET_QR_MIN_SIZE_PX / (Math.min(imageWidth, imageHeight) * TARGET_QR_SIZE_RATIO));
  const outputWidth = Math.round(imageWidth * scale);
  const outputHeight = Math.round(imageHeight * scale);
  const shorterSide = Math.min(outputWidth, outputHeight);
  const qrSize = Math.max(Math.round(shorterSide * TARGET_QR_SIZE_RATIO), TARGET_QR_MIN_SIZE_PX);
  const inset = Math.round(shorterSide * TARGET_QR_INSET_RATIO);
  return {
    outputWidth,
    outputHeight,
    qrSize,
    qrX: outputWidth - qrSize - inset,
    qrY: outputHeight - qrSize - inset,
  };
}

// The project name as a folder name: characters no file system accepts become spaces, and a name
// with nothing left falls back to a fixed one. Array.from keeps two-unit characters whole when cutting.
export const getProjectImagesFolderName = (projectName?: string | null): string => {
  const cleaned = (projectName ?? '')
    .replace(/[\\/:*?"<>|]/g, ' ')
    .replace(/\s+/g, ' ')
    .replace(/^[.\s]+|[.\s]+$/g, '');
  const folderName = Array.from(cleaned).slice(0, PROJECT_IMAGES_FOLDER_NAME_MAX_LENGTH).join('').trim();
  return folderName || PROJECT_IMAGES_FALLBACK_FOLDER_NAME;
}

// A JPEG target stays a JPEG (a photo written as PNG is several times larger); every other
// format is written as PNG, which every browser can produce and which keeps transparency.
export const getTargetImageOutputType = (mimeType: string): string => {
  return mimeType === 'image/jpeg' || mimeType === 'image/jpg' ? 'image/jpeg' : 'image/png';
}

export const getTargetImageFileName = (index: number, mimeType: string): string => {
  const extension = getTargetImageOutputType(mimeType) === 'image/jpeg' ? 'jpg' : 'png';
  return `target-${index + 1}.${extension}`;
}

// Where the logo goes in a QR code of the given size: a white square centred in the code, with
// the logo fitted inside it, padded and keeping its shape.
export const getQrLogoPlacement = (qrSize: number, logoWidth: number, logoHeight: number): QrLogoPlacement => {
  if (!(qrSize > 0) || !(logoWidth > 0) || !(logoHeight > 0)) {
    throw new Error(`Invalid QR logo size: QR ${qrSize}, logo ${logoWidth}x${logoHeight}`);
  }
  const backingSize = Math.round(qrSize * TARGET_QR_LOGO_RATIO);
  const backingOffset = Math.round((qrSize - backingSize) / 2);
  const padding = Math.round(backingSize * TARGET_QR_LOGO_PADDING_RATIO);
  const fitScale = Math.min((backingSize - 2 * padding) / logoWidth, (backingSize - 2 * padding) / logoHeight);
  const fittedWidth = Math.round(logoWidth * fitScale);
  const fittedHeight = Math.round(logoHeight * fitScale);
  return {
    backingX: backingOffset,
    backingY: backingOffset,
    backingSize,
    logoX: backingOffset + Math.round((backingSize - fittedWidth) / 2),
    logoY: backingOffset + Math.round((backingSize - fittedHeight) / 2),
    logoWidth: fittedWidth,
    logoHeight: fittedHeight,
  };
}

// Draws the target image and, in its bottom-right corner, the QR code on a white backing, with
// the logo (when given) in the middle of the code.
export const drawTargetImageWithQr = (source: TargetImageSource, qrCanvas: HTMLCanvasElement, logo?: TargetImageSource): HTMLCanvasElement => {
  const placement = getTargetQrPlacement(source.width, source.height);
  const canvas = document.createElement('canvas');
  canvas.width = placement.outputWidth;
  canvas.height = placement.outputHeight;
  const context = canvas.getContext('2d');
  if (!context) {
    throw new Error('Canvas 2D context is not available');
  }
  context.drawImage(source.image, 0, 0, placement.outputWidth, placement.outputHeight);
  context.fillStyle = TARGET_QR_BACKING_COLOR;
  context.fillRect(placement.qrX, placement.qrY, placement.qrSize, placement.qrSize);
  context.drawImage(qrCanvas, placement.qrX, placement.qrY, placement.qrSize, placement.qrSize);
  if (logo) {
    const logoPlacement = getQrLogoPlacement(placement.qrSize, logo.width, logo.height);
    context.fillRect(placement.qrX + logoPlacement.backingX, placement.qrY + logoPlacement.backingY, logoPlacement.backingSize, logoPlacement.backingSize);
    context.drawImage(logo.image, placement.qrX + logoPlacement.logoX, placement.qrY + logoPlacement.logoY, logoPlacement.logoWidth, logoPlacement.logoHeight);
  }
  return canvas;
}

const loadImage = (src: string): Promise<HTMLImageElement> => {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error(`The image could not be loaded: ${src}`));
    image.src = src;
  });
}

// Loads an image bundled with the app (same origin, so drawing it never taints a canvas).
export const loadImageFromUrl = async (url: string): Promise<TargetImageSource> => {
  const image = await loadImage(url);
  return { image, width: image.naturalWidth, height: image.naturalHeight };
}

const loadImageFromBlob = (blob: Blob): Promise<HTMLImageElement> => {
  return new Promise((resolve, reject) => {
    const objectUrl = URL.createObjectURL(blob);
    const image = new Image();
    image.onload = () => {
      URL.revokeObjectURL(objectUrl);
      resolve(image);
    };
    image.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      reject(new Error('The target image could not be decoded'));
    };
    image.src = objectUrl;
  });
}

const canvasToBytes = (canvas: HTMLCanvasElement, mimeType: string): Promise<Uint8Array> => {
  return new Promise((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (!blob) {
        reject(new Error('The image could not be written'));
        return;
      }
      const reader = new FileReader();
      reader.onload = () => {
        if (reader.result instanceof ArrayBuffer) {
          resolve(new Uint8Array(reader.result));
        } else {
          reject(new Error('The image could not be read back'));
        }
      };
      reader.onerror = () => reject(reader.error ?? new Error('The image could not be read back'));
      reader.readAsArrayBuffer(blob);
    }, mimeType, TARGET_IMAGE_JPEG_QUALITY);
  });
}

// One target image file in, the bytes of the same image with the QR code on it out.
export const composeTargetImageWithQr = async (file: Blob, qrCanvas: HTMLCanvasElement, logo?: TargetImageSource): Promise<Uint8Array> => {
  const image = await loadImageFromBlob(file);
  const canvas = drawTargetImageWithQr({ image, width: image.naturalWidth, height: image.naturalHeight }, qrCanvas, logo);
  return canvasToBytes(canvas, getTargetImageOutputType(file.type));
}

// Hands a file built in the browser to the browser's own download.
export const saveBytesAs = (bytes: Uint8Array, mimeType: string, fileName: string): void => {
  const objectUrl = URL.createObjectURL(new Blob([bytes], { type: mimeType }));
  const link = document.createElement('a');
  link.href = objectUrl;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  // Not straight away: some browsers start the download a moment after the click.
  setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);
}

export const formatBytesToMB = (bytes: number | null | undefined): string | null => {
  if (bytes === null || bytes === undefined) return null;
  return `${(bytes / (1024 * 1024)).toFixed(1)}MB`;
}

// PAYMENT.md Phase 5 -- 30-day boundary matches the d30 warning threshold the backend's daily
// expiry-notification job uses, so "expiring soon" in the editor lines up with when the owner
// would have already received the first warning email.
const EXPIRY_WARNING_DAYS = 30;

export const getExpiryStatus = (expiresAt: string | null): IExpiryStatus => {
  if (!expiresAt) {
    return { label: 'No expiry', isExpired: false, isExpiringSoon: false, daysRemaining: null };
  }

  const msRemaining = new Date(expiresAt).getTime() - Date.now();
  if (msRemaining <= 0) {
    return { label: 'Expired', isExpired: true, isExpiringSoon: false, daysRemaining: 0 };
  }

  const daysRemaining = Math.ceil(msRemaining / (24 * 60 * 60 * 1000));
  const isExpiringSoon = daysRemaining <= EXPIRY_WARNING_DAYS;
  return {
    label: isExpiringSoon ? 'Expiring soon' : 'Active',
    isExpired: false,
    isExpiringSoon,
    daysRemaining,
  };
}

const ADMIN_LIST_MAX_LIMIT = 100;

// Shared by admin_users/admin_projects/admin_tickets screens -- normalizes pagination and drops
// empty filter values so requests match the backend's Record<string, unknown> match-building
// (admin.service.ts), which treats an absent key differently from an empty-string one.
export const buildAdminListQuery = (params: Record<string, unknown>): Record<string, unknown> => {
  const { skip, limit, search, ...filters } = params;

  const query: Record<string, unknown> = {
    skip: typeof skip === 'number' ? skip : 0,
    limit: Math.min(typeof limit === 'number' ? limit : 20, ADMIN_LIST_MAX_LIMIT),
  };

  if (typeof search === 'string' && search.trim().length > 0) {
    query.search = search.trim();
  }

  Object.keys(filters).forEach((key) => {
    const value = filters[key];
    if (value !== undefined && value !== null && value !== '') {
      query[key] = value;
    }
  });

  return query;
}

export const formatViewCount = (count: number | null | undefined): string => {
  const value = count || 0;
  if (value < 1000) return `${value}`;
  if (value < 1000000) return `${parseFloat((value / 1000).toFixed(1))}k`;
  return `${parseFloat((value / 1000000).toFixed(1))}M`;
}

// Noise filter for reality.imagefound, which re-fires on marginal tracking (target at the frame
// edge, low light, camera shake) as well as genuine re-looks. Not dedupe -- every real re-look is
// still counted, only tracking jitter within the window is dropped.
export const RE_DETECT_DEBOUNCE_MS = 3000;

export const shouldRecordTargetView = (
  lastRecordedAt: Record<string, number>,
  targetId: string,
  now: number
): boolean => {
  const previous = lastRecordedAt[targetId];
  return previous === undefined || now - previous >= RE_DETECT_DEBOUNCE_MS;
};

export const recordTargetViewSafely = (recordFn: () => Promise<unknown>): void => {
  recordFn().catch((error) => console.error('Failed to record target view:', error));
}

// A target with no target_views record has never been scanned -- the backend sends null rather
// than a date, so the reader sees "Never viewed" instead of a fabricated timestamp.
export const formatLastViewedAt = (lastViewedAt: string | null): string => {
  if (!lastViewedAt) return 'Never viewed';

  const date = new Date(lastViewedAt);
  if (Number.isNaN(date.getTime())) return 'Never viewed';

  return date
    .toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: 'numeric',
      hour12: true,
    })
    .replace(',', ' at');
}


// The visit-check API sends snake_case keys; a missing counter counts as 0 and a missing response never
// allows a visit.
export const parseVisitCheck = (response: Partial<VisitCheckResponse> | undefined): ParsedVisitCheck => ({
  allowed: response?.status === 'success',
  unlimited: response?.payment_status === true,
  remainingVisits: response?.remaining_visits ?? 0,
  maxVisits: response?.max_visits ?? 0,
});

// The API puts its reason in `response.data.message`; anything else (network error, a throw in
// the browser) gets the caller's fallback.
export const getApiErrorMessage = (error: unknown, fallback: string): string => {
  if (typeof error !== 'object' || error === null || !('response' in error)) return fallback;
  const { response } = error;
  if (typeof response !== 'object' || response === null || !('data' in response)) return fallback;
  const { data } = response;
  if (typeof data !== 'object' || data === null || !('message' in data)) return fallback;
  return typeof data.message === 'string' && data.message ? data.message : fallback;
}

// An object's stored type is a plain string; narrow it to the selection's PropertiesTypes, or null when it is not one.
export const toPropertiesType = (type: string): PropertiesTypes | null =>
    Object.values(PropertiesTypes).find((value) => value === type) ?? null;
